import { useState, useEffect, useRef } from 'react';

const SLIDER_NAMES = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
];

const SLIDER_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

function tryLoad(path: string): Promise<string | null> {
  return new Promise((resolve) => {
    const url = `/${path}?t=${Date.now()}`;
    const img = new Image();
    img.onload = () => resolve(path);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Try one name: .jpg first, then other extensions if needed. */
async function tryName(name: string): Promise<string | null> {
  for (const ext of SLIDER_EXTENSIONS) {
    const path = `slider/${name}${ext}`;
    const result = await tryLoad(path);
    if (result) return result;
  }
  return null;
}

/**
 * Discovers which slider images exist in /slider/ in order (first, second, third, ...).
 * All names are checked in parallel; the slider appears as soon as the first image loads.
 */
export function useSliderImages(): { slidePaths: string[]; isLoading: boolean } {
  const [resultsByIndex, setResultsByIndex] = useState<(string | null)[]>(
    () => SLIDER_NAMES.map(() => null)
  );
  const [discoveryDone, setDiscoveryDone] = useState(false);
  const pendingRef = useRef(SLIDER_NAMES.length);

  const slidePaths = resultsByIndex.filter((r): r is string => r !== null);
  const isLoading = slidePaths.length === 0 && !discoveryDone;

  useEffect(() => {
    let cancelled = false;

    SLIDER_NAMES.forEach((name, index) => {
      tryName(name).then((path) => {
        if (cancelled) return;
        if (path) {
          setResultsByIndex((prev) => {
            const next = [...prev];
            next[index] = path;
            return next;
          });
        }
        pendingRef.current -= 1;
        if (pendingRef.current === 0) {
          setDiscoveryDone(true);
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { slidePaths, isLoading };
}
